import { APP_MAIN_CONTENT_ID } from './testUtils';

export const INTEREST_RESPONSE_KEYS = new Set([
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'ArrowDown'
]);

/**
 * @param {KeyboardEvent} e
 * @param {{ isConfirmOpen?: () => boolean }} [options]
 */
export function shouldHandleInterestResponseKey(e, { isConfirmOpen } = {}) {
  if (!INTEREST_RESPONSE_KEYS.has(e.key)) return false;
  if (e.ctrlKey || e.metaKey || e.altKey) return false;
  if (isConfirmOpen?.()) return false;

  const target = e.target;
  if (!(target instanceof Element)) return false;
  if (target.closest('input, textarea, select, [contenteditable="true"]')) return false;
  if (target.closest('nav') || target.closest('footer')) return false;

  const inMain = target.closest(`#${APP_MAIN_CONTENT_ID}`);
  if (!inMain && target !== document.body && target !== document.documentElement) {
    return false;
  }

  return true;
}

/** @param {string} key */
export function interestResponseValueFromKey(key) {
  if (key === 'ArrowRight') return 1;
  if (key === 'ArrowDown') return 0;
  if (key === 'ArrowLeft' || key === 'ArrowUp') return -1;
  return null;
}

/** @param {string} key */
export function interestIntroHintFromKey(key) {
  if (key === 'ArrowRight') return 'agree';
  if (key === 'ArrowDown') return 'neutral';
  if (key === 'ArrowLeft' || key === 'ArrowUp') return 'disagree';
  return null;
}

/**
 * Capture-phase listener so Chromium applies preventDefault before default scroll.
 * @param {(value: -1 | 0 | 1) => void} onResponse
 * @param {{ isConfirmOpen?: () => boolean }} [options]
 */
export function bindInterestResponseKeyboard(onResponse, options = {}) {
  const onKeyDown = (e) => {
    if (!shouldHandleInterestResponseKey(e, options)) return;
    const value = interestResponseValueFromKey(e.key);
    if (value === null) return;
    e.preventDefault();
    e.stopPropagation();
    onResponse(value);
  };

  window.addEventListener('keydown', onKeyDown, true);
  return () => window.removeEventListener('keydown', onKeyDown, true);
}

/**
 * @param {(hint: 'agree' | 'neutral' | 'disagree') => void} onHint
 */
export function bindInterestIntroKeyboard(onHint) {
  const onKeyDown = (e) => {
    if (!shouldHandleInterestResponseKey(e)) return;
    const hint = interestIntroHintFromKey(e.key);
    if (!hint) return;
    e.preventDefault();
    e.stopPropagation();
    onHint(hint);
  };

  window.addEventListener('keydown', onKeyDown, true);
  return () => window.removeEventListener('keydown', onKeyDown, true);
}
